import { Box, FormControl, Grid, MenuItem, styled, Typography } from "@mui/material";
import { AppSelect } from "components/common/Input";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import theme from "lib/theme";
import AssetCart from "./AssetCart";
import { FieldConfig, FieldInputProps, useFormikContext } from "formik";
import { AssetRetrieval, defaultDevice } from "hooks/assetRetrievals/useAssetRetrieval";
import { BoxType, DeviceType } from "types/assetRetrieval";
import Helper from "./Helper";
import { useCallback, useMemo } from "react";
import SimpleBoxInputs from "./SimpleBoxInputs";
import BulkBoxInputs from "./BulkBoxInputs";
import useFeatureFlag from "hooks/useFeatureFlag";

export type GetFieldPropsType = <Value = any>(props: string | FieldConfig<Value>) => FieldInputProps<Value>;

interface StartOrderProps {
  goToNextStep: () => void;
}

export const Label = styled(Typography)(() => ({
  marginBottom: 10,
}));

type MainBoxType =
  | BoxType.laptop
  | BoxType.tablet
  | BoxType.mobilePhone
  | BoxType.monitor
  | BoxType.desktop
  | BoxType.bulk;
type MainDeviceType = Record<MainBoxType, { label: string; deviceType: DeviceType }>;

const newMainDeviceTypes: MainDeviceType = {
  [BoxType.laptop]: { label: 'Laptop Box - 18" x 17" x 3"', deviceType: DeviceType.laptop },
  [BoxType.tablet]: { label: 'Tablet Box - 18" x 17" x 3"', deviceType: DeviceType.tablet },
  [BoxType.mobilePhone]: { label: 'Mobile Phone Box - 10" x 6" x 1"', deviceType: DeviceType.mobile_phone },
  [BoxType.monitor]: { label: 'Monitor Box - 38" x 19" x 8"', deviceType: DeviceType.monitor },
  [BoxType.desktop]: { label: 'Desktop Box - 21" x 19" x 11"', deviceType: DeviceType.desktop },
  [BoxType.bulk]: { label: 'Bulk Box (10 items) - 16.5" x 16.5" x 16.5"', deviceType: DeviceType.laptop },
};

const mainDeviceTypes: MainDeviceType = {
  [BoxType.laptop]: { label: 'Laptop Box - 18" x 17" x 3"', deviceType: DeviceType.laptop },
  [BoxType.tablet]: { label: 'Tablet Box - 18" x 17" x 3"', deviceType: DeviceType.tablet },
  [BoxType.mobilePhone]: { label: 'Mobile Phone Box - 10" x 6" x 1"', deviceType: DeviceType.mobile_phone },
  [BoxType.monitor]: { label: 'Monitor Box - 38" x 19" x 8"', deviceType: DeviceType.monitor },
  [BoxType.desktop]: { label: 'Desktop Box - 21" x 19" x 11"', deviceType: DeviceType.desktop },
  [BoxType.bulk]: { label: 'Bulk Box (10 items) - 16.5" x 16.5" x 16.5"', deviceType: DeviceType.laptop },
};

const SelectBox = ({ goToNextStep }: StartOrderProps) => {
  const { getFieldProps, errors, touched, setFieldValue, values } = useFormikContext<AssetRetrieval>();
  const { enabled: boomerangBulkBoxEnabled } = useFeatureFlag("boomerang_bulk_box_enabled");
  const { enabled: tabletBoxEnabled } = useFeatureFlag("customer_retrievals_tablet_box_option");
  const deviceTypes = useMemo(() => (tabletBoxEnabled ? newMainDeviceTypes : mainDeviceTypes), [tabletBoxEnabled]);
  const deviceOptions = useMemo(() => {
    if (boomerangBulkBoxEnabled) {
      return deviceTypes;
    }

    return Object.fromEntries(Object.entries(deviceTypes).filter(([key]) => key !== BoxType.bulk));
  }, [boomerangBulkBoxEnabled]);

  const onBoxTypeChange = useCallback(
    (e: { target: { value: string } }) => {
      const mainDevice = {
        ...(values.devices?.[0] || defaultDevice),
        deviceType: deviceOptions[e.target.value as MainBoxType].deviceType,
      };
      setFieldValue("devices", [mainDevice]);
      setFieldValue("boxType", e.target.value);
    },
    [values.devices, values.devices?.[0], setFieldValue, deviceOptions]
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 4,
        alignItems: "start",
        [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
      }}
    >
      <Box
        sx={{
          py: 3,
          px: 4,
          background: "white",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
        }}
      >
        <Grid container>
          <Grid pr={1}>
            <ArrowForwardIcon />
          </Grid>
          <Grid xs={11}>
            <Typography variant="h5" fontWeight="bold">
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  fontSize: "20px",
                  fontWeight: "600",
                }}
              >
                What's being sent back
              </Box>
            </Typography>

            <Typography variant="body1">
              Laptop/Tablet Box can fit a laptop (up to 16') and one other small device such as a phone or tablet.
            </Typography>

            <Box mt={2}>
              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
                <FormControl variant="standard">
                  <Label>Select Box Type</Label>
                  <AppSelect
                    {...getFieldProps<string>("boxType")}
                    onChange={onBoxTypeChange}
                    placeholder={"Ex: Laptop/Tablet Box"}
                  >
                    {Object.entries(deviceOptions).map(([key, object]) => (
                      <MenuItem key={key} value={key}>
                        {object.label}
                      </MenuItem>
                    ))}
                  </AppSelect>
                </FormControl>
              </Box>

              {values.boxType && values.boxType !== BoxType.bulk && (
                <SimpleBoxInputs
                  getFieldProps={getFieldProps}
                  touched={touched}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              )}
              {values.boxType && values.boxType === BoxType.bulk && (
                <BulkBoxInputs getFieldProps={getFieldProps} touched={touched} errors={errors} values={values} />
              )}
            </Box>
            <Helper boxType={values.boxType as BoxType} />
          </Grid>
        </Grid>
      </Box>

      <AssetCart
        onContinue={goToNextStep}
        disableContinue={Boolean(
          errors.manufacturer || errors.model || errors.serialNumber || errors.insuranceOption || errors.boxType
        )}
      />
    </Box>
  );
};

export default SelectBox;
